import { EditableText } from '@/components/editor/EditableText';
import { FICHE_COLOR_HEX } from '@/lib/colors';
import type { Bullet, FicheColor } from '@pharmafiche/schema';

interface BulletsProps {
  bullets: Bullet[];
  /** Default color for bullet dots and **bold** segments */
  accent: FicheColor;
  /** Whether the dots themselves should be inked (default true) */
  inkBorder?: boolean;
  /** Use slightly smaller font for tight columns (drug cards). */
  dense?: boolean;
  /** Optional commit handler — when provided, each bullet becomes editable. */
  onBulletChange?: (bulletId: string, text: string) => void;
}

/**
 * The hand-drawn list with a colored 7px dot + ink border at the start of each line.
 * Implements `ul.bullets` from the prototype. When in edit mode, each bullet's
 * text becomes a contenteditable EditableText (committed on blur).
 */
export function Bullets({
  bullets,
  accent,
  inkBorder = true,
  dense = false,
  onBulletChange,
}: BulletsProps) {
  const defaultBold = FICHE_COLOR_HEX[accent];
  const fontPx = dense ? 13.5 : 15.5;
  const topDot = dense ? 8 : 9;
  return (
    <ul className="m-0 mt-1 p-0 list-none">
      {bullets.map((b) => {
        const dotColor = b.emphasis ? FICHE_COLOR_HEX[b.emphasis] : defaultBold;
        const boldColor = b.emphasis ? FICHE_COLOR_HEX[b.emphasis] : defaultBold;
        return (
          <li
            key={b.id}
            className="relative pl-[18px] my-[5px] font-hand leading-[1.35] text-ink"
            style={{
              fontSize: fontPx,
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              hyphens: 'auto',
            }}
            lang="fr"
            data-bullet-id={b.id}
          >
            <span
              aria-hidden
              className="absolute left-[2px] w-[7px] h-[7px] rounded-full"
              style={{
                top: topDot,
                background: dotColor,
                ...(inkBorder ? { border: '1.5px solid #1f2330' } : {}),
              }}
            />
            <EditableText
              value={b.text}
              boldColor={boldColor}
              ariaLabel="Bullet"
              onCommit={(next) => onBulletChange?.(b.id, next)}
            />
          </li>
        );
      })}
    </ul>
  );
}
